import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { UserFragment } from "@/codegen/graphql/fragments/User.fragment";
import type { FlowType } from "@/lib/funnelFlow";
import type { ABTestCampaignId } from "@/data/abTestCampaigns.data";
import {
  LegacyStorageKeys,
  getLegacyStorageValue,
} from "@/hooks/useLegacyStorage";

type UserFragmentType = ResultOf<typeof UserFragment>;

export interface AuthStoreState {
  accessToken: string | null;
  deviceId: string | null;
  email: string | null;
  currentUser: UserFragmentType | null;
  abTestVariants: Record<string, string> | null;
  // Standalone funnel flag (e.g. exp_wmq). Sticky: persisted and intentionally
  // left out of resetAuthStore so it survives logout.
  funnelFlow: FlowType;
}

interface AuthStore extends AuthStoreState {
  /** Set or clear (pass undefined) a single AB test variant entry. */
  setABTestVariant: (
    campaignId: ABTestCampaignId,
    variant: string | undefined,
  ) => void;
  getABTestVariant: (campaignId: ABTestCampaignId) => string | undefined;
  resetAuthStore: () => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        accessToken: null,
        deviceId: null,
        email: getLegacyStorageValue(LegacyStorageKeys.EMAIL) || "",
        currentUser: null,
        abTestVariants: null,
        funnelFlow: "default",
        setABTestVariant: (campaignId, variant) =>
          set((s) => {
            const next = { ...(s.abTestVariants ?? {}) };
            if (variant === undefined) delete next[campaignId];
            else next[campaignId] = variant;
            return { abTestVariants: next };
          }),
        getABTestVariant: (campaignId) => get().abTestVariants?.[campaignId],
        resetAuthStore: () =>
          set({
            accessToken: null,
            currentUser: null,
            email: null,
          }),
      }),
      {
        name: "auth-storage",
        version: 2,
        // v0→v1: deeplink dropped — the tokenized OneLink now comes from the
        // backend oneLinkDeeplinkUrl query, not a persisted store field.
        // v1→v2: the exp_md funnel was retired (SHO-713) — a device still
        // tagged with it goes back to the default flow, not to exp_wmq (it was
        // never in that experiment).
        migrate: (persistedState: unknown, version: number) => {
          const s = persistedState as {
            deeplink?: string;
            funnelFlow?: string;
          };
          if (version < 1) delete s.deeplink;
          if (version < 2 && s.funnelFlow === "exp_md")
            s.funnelFlow = "default";
          return persistedState;
        },
      },
    ),
  ),
);

export const useAccessToken = () => useAuthStore((state) => state.accessToken);

export const useIsAuthenticated = () => !!useAccessToken();

// A real logged-in account (vs an anonymous device user, which also holds an
// access token): the user has at least one linked social provider. Never the
// email, which a device user can carry without ever logging in (SHO-853).
export const isLoggedIn = () =>
  !!useAuthStore.getState().currentUser?.providers.length;

export const useIsLoggedIn = () =>
  useAuthStore((state) => !!state.currentUser?.providers.length);

export const useFunnelFlow = () => useAuthStore((state) => state.funnelFlow);
